import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import Swiper from 'react-id-swiper'
import ResponsiveImage from 'components/images/responsive-image'
import ShopTheLook from 'components/pdp/carousel-images/shop-the-look'
import SvgIcon from 'components/shared/svg-icon'
import env from 'config/env'
import { toggleBodyFreezing } from 'lib/utils/common/commonUtils'
import useSkinnyBanners from 'components/skinny-banner/useSkinnyBanner'
import { connect } from 'react-redux'
import { withI18next } from 'lib/i18n/withI18next'
import ProductVirtualTryOn from 'components/pdp/product-virtual-try-on/ProductVirtualTryOn'
import ImageCarouselVideo from './image-carousel-video/ImageCarouselVideo'
import styles from './styles.scss'
import segmentProductImageGalleryViewed from '../carousel-images-segment/carouselImagesSegment'
import ImageCarouselAutoPlayVideo from './image-carousel-auto-play-video/ImageCarouselAutoPlayVideo'
import syncSkinnyBannerLayout from './skinnyBannerLayout'

const swiperState = { changing: false }

const ImageCarousel = ({
  t,
  id,
  user,
  video,
  images,
  isPhone,
  is_italist,
  isGuestMode,
  isOpenVirtualTryOn,
  onLastImageSwipeUp,
  isShownShopTheLook,
  virtual_try_on_enabled,
  shopLookCtaEventTracking,
}) => {
  // ref
  const swiperRef = useRef(null)
  const videoRef = useRef(null)

  // states
  const [banners, isFetched] = useSkinnyBanners(
    isGuestMode,
    t,
    user?.customer_groups,
  )
  const isHasSkinnyBanner =
    isFetched && banners.length > 0 && banners?.[0]?.active_from

  useEffect(
    () => syncSkinnyBannerLayout(swiperRef.current?.swiper, isHasSkinnyBanner),
    [isHasSkinnyBanner, id],
  )

  const [curIdx, setCurIdx] = useState(1)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const totalImg = images.length
  const isHiddenShopTheLook = curIdx > totalImg

  const onSlideChange = () => {
    const { activeIndex } = swiperRef?.current?.swiper || {}
    const currentIndex = (activeIndex || 0) + 1
    setCurIdx(currentIndex)

    const currentImageTypeSlideChange = images[currentIndex - 1].image_type
    segmentProductImageGalleryViewed({
      ref_id: id,
      ref_type: 'pdp',
      id_product: id,
      image_type: currentImageTypeSlideChange,
    })

    if (swiperRef.current) {
      swiperRef.current.swiper.allowTouchMove = true
    }
  }

  const swiperOptions = {
    slidesPerView: 1,
    direction: 'vertical',
    shouldSwiperUpdate: true,
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
    on: {
      slideChange: onSlideChange,
      transitionStart: () => {
        swiperState.changing = true
      },
      transitionEnd: () => {
        swiperState.changing = false
      },
      touchEnd: () => {
        // Check if the user is swiping up on the last slide
        const { activeIndex, touches } = swiperRef?.current?.swiper || {}
        const isLastSlide = activeIndex === images.length - 1
        const isSwipeUp = touches.diff < 0

        if (isLastSlide && isSwipeUp) {
          onLastImageSwipeUp()
        }
      },
    },
    containerClass: `carousel__swiper-container swiper-container`,
  }

  // handlers
  const handleClickParentVideo = (e) => {
    if (swiperState.changing) {
      return
    }

    if (videoRef?.current) {
      videoRef.current.onParentClick(e)
    }
  }

  const handleTapFullImage = (e) => {
    if (swiperState.changing) {
      return
    }

    if (e?.preventDefault) {
      e.preventDefault()
    }

    if (videoRef?.current) {
      if (isFullScreen) {
        videoRef.current.resetVideo()
      }
    }

    toggleBodyFreezing(!isFullScreen)
    setIsFullScreen(!isFullScreen)
  }

  useEffect(() => {
    swiperRef.current?.swiper?.slideTo(0)
  }, [id])

  if (!images.length) {
    return null
  }

  return (
    <React.Fragment>
      <div
        className={clsx('carousel__container', {
          'full-screen': isFullScreen,
        })}
        onClick={isFullScreen ? null : handleTapFullImage}
        onKeyDown={isFullScreen ? null : handleTapFullImage}
      >
        <style jsx>{styles}</style>
        <SvgIcon
          className={clsx('svg-close', {
            active: isFullScreen,
          })}
          onClick={isFullScreen ? handleTapFullImage : null}
        />
        <Swiper key={id} ref={swiperRef} {...swiperOptions}>
          {images.map((img, imgIdx) => {
            const isVideo = Boolean(img.full_video_path)

            if (isVideo) {
              return (
                <div className="carousel-img__container" key={img.full_path}>
                  <ImageCarouselAutoPlayVideo
                    poster={img.full_path}
                    src={img.full_video_path}
                    className={clsx('carousel__video', {
                      'carousel__video__with-skinny-banner': isHasSkinnyBanner,
                    })}
                  />
                </div>
              )
            }

            return (
              <div className="carousel-img__container" key={img.full_path}>
                <ResponsiveImage
                  className={clsx('carousel__img', {
                    'carousel__img__with-skinny-banner': isHasSkinnyBanner,
                    'carousel__img__italist-product': is_italist,
                  })}
                  src={img.full_path}
                  imgRatio={1.33}
                  imgStyle={{ objectFit: 'contain', objectPosition: 'center' }}
                  lazy
                  lazyThreshold={0.1}
                  fetchPriority={imgIdx === 0 ? 'high' : undefined}
                />
              </div>
            )
          })}
          {Object.keys(video || {}).length > 0 && (
            <ImageCarouselVideo
              videoRef={videoRef}
              onVideoParentClick={handleClickParentVideo}
              poster={`${env.IMG_HOST}${video.cover_image_path}`}
              src={`${env.MEDIA_CLOUDFRONT_URL}${video.video_path}`}
            />
          )}
        </Swiper>
      </div>
      {isShownShopTheLook && (
        <ShopTheLook
          productId={id}
          isPhone={isPhone}
          isExpanded
          isHiddenShopTheLook={isHiddenShopTheLook}
          shopLookCtaEventTracking={shopLookCtaEventTracking}
        />
      )}
      {isPhone && virtual_try_on_enabled && (
        <ProductVirtualTryOn
          productId={id}
          isPhone={isPhone}
          isVirtualTryOn={isOpenVirtualTryOn}
        />
      )}
    </React.Fragment>
  )
}

ImageCarousel.defaultProps = {
  video: {},
  isShownShopTheLook: false,
  onLastImageSwipeUp: undefined,
  shopLookCtaEventTracking: null,
  isGuestMode: false,
  user: null,
  is_italist: false,
  isOpenVirtualTryOn: false,
  virtual_try_on_enabled: false,
}

ImageCarousel.propTypes = {
  is_italist: PropTypes.bool,
  isPhone: PropTypes.bool.isRequired,
  isShownShopTheLook: PropTypes.bool,
  onLastImageSwipeUp: PropTypes.func,
  shopLookCtaEventTracking: PropTypes.func,
  video: PropTypes.shape({
    video_path: PropTypes.string,
    cover_image_path: PropTypes.string,
  }),
  images: PropTypes.arrayOf(
    PropTypes.shape({
      full_path: PropTypes.string.isRequired,
      image_type: PropTypes.string.isRequired,
    }),
  ).isRequired,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isGuestMode: PropTypes.bool,
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id_customer: PropTypes.number,
    customer_groups: PropTypes.arrayOf(PropTypes.number),
  }),
  isOpenVirtualTryOn: PropTypes.bool,
  virtual_try_on_enabled: PropTypes.bool,
}

const Extended = withI18next()(ImageCarousel)

export default connect((state) => ({
  categoryData: state.category.payload,
  isGuestMode: state.auth.isGuestMode,
  isPhone: state.device.isPhone,
  user: state.auth.user,
}))(Extended)
